import React, { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";

import {
  QuicktalesTableHeadCells,
  getComparator,
  stableSort,
} from "../constants";
import TablePaginationSection from "../common/TablePaginationSection";
import ImagePreviewModal from "../common/ImagePreviewModal";
import AudioModal from "../common/AudioModal";
import CommonTableHead from "../common/CommonTableHead";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

export default function CommonTable({
  selected,
  setSelected,
  tableData,
  setTableData,
  moduleDetails,
  module,
  CommonTableRow,
  showPosition,
  // showPosition = true,
  removeRow,
  tableHeadData,
  handleSavePosition,
  albumsTable,
  initialOrder = "asc",
  initialOrderBy = "",
  hideRemoveLink,
  onTitleClick,
}) {
  const [order, setOrder] = useState(initialOrder);
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  console.log("orderBy:", orderBy);
  // Initialize column widths based on tableHeadData or default QuicktalesTableHeadCells
  const initializeColumnWidths = () => {
    const headCells = tableHeadData || QuicktalesTableHeadCells;
    const widths = {};
    headCells.forEach(cell => {
      widths[cell.id] = cell.width || 200;
    });
    return widths;
  };
  
  const [columnWidths, setColumnWidths] = useState(initializeColumnWidths);

  const handleColumnResize = (columnId, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: newWidth
    }));
  };

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
    setSelectedImage(null);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
    setSelectedAudioFile(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page - 1 > 0
      ? Math.max(0, (1 + page - 1) * rowsPerPage - tableData.length)
      : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(
        tableData && tableData.length > 0 ? tableData : [],
        getComparator(order, orderBy)
      ).slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, tableData]
  );
  console.log("visibleRows",visibleRows);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = visibleRows.map((n) => n.id);
      // const newSelected = reactionsInOrg.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClickRow = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    // console.log("selectedIndex", selectedIndex);
    // console.log("id", id);
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleDragEnd = async (e) => {
    if (!e.destination) {
      console.log("No destination detected.");
      return;
    }

    // Log the source and destination indices from the drag event
    console.log("Event Source Index:", e.source.index);
    console.log("Event Destination Index:", e.destination.index);

    let tempData = Array.from(visibleRows);
    let sourceIndex = e.source.index;
    let destinationIndex = e.destination.index;

    // If the source and destination indices are the same, there's no need to proceed
    if (sourceIndex === destinationIndex) {
      console.log(
        "Source and destination indices are the same, no changes needed."
      );
      return;
    }

    // Remove the item from its original position and insert it at the new position
    let [source_data] = tempData.splice(sourceIndex, 1);
    tempData.splice(destinationIndex, 0, source_data);

    const sourceId = source_data.id;
    const destination_data = tempData[destinationIndex];
    const destinationId = destination_data.id;

    if (module === "area") {
      // Call handleSavePosition with updated albumsAndQTs array
      if (handleSavePosition) {
        await handleSavePosition(tempData);
      }
    } else {
      // Logic for other modules - extract IDs from the reordered tempData
      const reorderedIds = tempData.map(item => item.id);
      
      console.log("DRAG DEBUG: Reordered IDs from tempData:", reorderedIds);
      
      // Call handleSavePosition with the reordered IDs array
      if (handleSavePosition) {
        try {
          console.log("DRAG DEBUG: Calling handleSavePosition with:", reorderedIds);
          await handleSavePosition(reorderedIds);
        } catch (error) {
          console.error("Error in handleSavePosition:", error);
        }
      } else {
        console.log("DRAG DEBUG: handleSavePosition is not defined");
      }
    }

    // Update the table data with the new order
    console.log("Updated table data:", tempData);
    setTableData(tempData);
  };

  console.log("visibleRows", visibleRows);

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Paper sx={tablePaperContainerStyles}>
          <TableContainer style={tableContainerStyles}>
            <Table stickyHeader>
              <CommonTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                rowCount={visibleRows.length}
                onRequestSort={handleRequestSort}
                tableHeadCells={
                  tableHeadData ? tableHeadData : QuicktalesTableHeadCells
                }
                showPosition={showPosition}
                columnWidths={columnWidths}
                onColumnResize={handleColumnResize}
              />
              <Droppable droppableId="droppable-1">
                {(provider) => (
                  <TableBody
                    ref={provider.innerRef}
                    {...provider.droppableProps}
                  >
                    {visibleRows.map((row, index) => {
                      const isItemSelected = isSelected(row.id);
                      const labelId = `enhanced-table-checkbox-${index}`;
                      const numberType = index % 2 == 0;

                      return (
                        <Draggable
                          key={row?.id}
                          draggableId={row?.id}
                          index={index}
                        >
                          {(provided) => (
                            <CommonTableRow
                              isItemSelected={isItemSelected}
                              row={row}
                              handleClickRow={handleClickRow}
                              numberType={numberType}
                              labelId={labelId}
                              key={index}
                              position={index}
                              handleImagePreviewModalOpen={
                                handleImagePreviewModalOpen
                              }
                              handleAudioModalOpen={handleAudioModalOpen}
                              setSelectedAudioFile={setSelectedAudioFile}
                              setSelectedImage={setSelectedImage}
                              showPosition={showPosition}
                              removeRow={removeRow}
                              provided={provided}
                              columnWidths={columnWidths}
                              hideRemoveLink={hideRemoveLink}
                              onTitleClick={onTitleClick}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{
                          height: 53 * emptyRows,
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                    {visibleRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No results found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </TableContainer>
          <Box>
            <TablePaginationSection
              count={tableData && Math.ceil(tableData.length / rowsPerPage)}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              total={tableData.length ?? 0}
            />
          </Box>

          <ImagePreviewModal
            modalOpen={imagePreviewModal}
            handleClose={handleImagePreviewModalClose}
            image={selectedImage && selectedImage}
          />
          <AudioModal
            modalOpen={audioModal}
            handleClose={handleAudioModalClose}
            audioFile={selectedAudioFile && selectedAudioFile}
          />
        </Paper>
      </DragDropContext>
    </>
  );
}

const tablePaperContainerStyles = {
  // maxWidth: "100%",
  width: "100%",
  mb: 1,
  border: "none",
  boxShadow: "none",
  mt: 1,
};
const tableContainerStyles = {
  border: "none",
  boxShadow: "none",
  maxHeight: "calc(90vh - 180px)",
  maxWidth: "calc(90vw - 120px)"
};
